import type { WorkBook } from 'xlsx';
import { ARCHIVE_COLUMNS, SALES_COLUMNS, normalizeAddress } from './deliveryCalc';
import type { SheetRow, SheetTable } from './deliveryCalc';

/**
 * Чтение и выгрузка Excel для «Сверки доставок». Файлы читаются только в браузере и на сервер не уходят.
 * Библиотека xlsx тяжелая, поэтому подгружается при первом чтении, а не при открытии вкладки.
 */

export type DeliveryFileKind = 'sales' | 'archive';

interface FileSpec {
  /** Номер строки (с нуля), где лежат заголовки */
  headerRow: number;
  /** Колонки, без которых расчет невозможен */
  required: string[];
  title: string;
}

const FILE_SPECS: Record<DeliveryFileKind, FileSpec> = {
  sales: {
    headerRow: 4,
    required: [SALES_COLUMNS.orderId, SALES_COLUMNS.address, SALES_COLUMNS.amount],
    title: 'Sales Report'
  },
  archive: {
    headerRow: 0,
    required: [ARCHIVE_COLUMNS.orderId, ARCHIVE_COLUMNS.address, ARCHIVE_COLUMNS.warehouse],
    title: 'Archive Orders'
  }
};

/** Все известные колонки: заголовок в файле приводится к каноническому написанию (регистр, лишние пробелы) */
const CANONICAL_HEADERS: Record<string, string> = {};
[SALES_COLUMNS, ARCHIVE_COLUMNS].forEach((columns) => {
  Object.keys(columns).forEach((key) => {
    const name = (columns as Record<string, string>)[key];
    CANONICAL_HEADERS[normalizeAddress(name)] = name;
  });
});

function cellToText(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

/** Заголовки строки: непустые, без повторов, вместе с номером колонки (пустые колонки не сдвигают значения) */
function readHeaders(headerCells: unknown[]): { name: string; column: number }[] {
  const used: Record<string, boolean> = {};
  const headers: { name: string; column: number }[] = [];

  headerCells.forEach((cell, column) => {
    const text = cellToText(cell).trim();
    if (text === '') return;

    let name = CANONICAL_HEADERS[normalizeAddress(text)] || text;
    // Повтор названия не должен затереть данные предыдущей колонки
    for (let n = 2; used[name]; n += 1) name = `${text} (${n})`;
    used[name] = true;
    headers.push({ name, column });
  });

  return headers;
}

function parseSheet(rowsOfCells: unknown[][], spec: FileSpec): SheetTable | { missing: string[] } {
  const headers = readHeaders((rowsOfCells[spec.headerRow] as unknown[]) || []);
  const names = headers.map((h) => h.name);
  const missing = spec.required.filter((column) => names.indexOf(column) < 0);
  if (missing.length > 0) return { missing };

  const rows: SheetRow[] = [];
  for (let i = spec.headerRow + 1; i < rowsOfCells.length; i += 1) {
    const cells = rowsOfCells[i] as unknown[];
    const row: SheetRow = {};
    let filled = false;
    headers.forEach((h) => {
      const text = cellToText(cells[h.column]);
      if (text.trim() !== '') filled = true;
      row[h.name] = text;
    });
    // Полностью пустые строки (хвост листа, разделители) в расчете не участвуют
    if (filled) rows.push(row);
  }

  return { headers: names, rows };
}

/**
 * Читает первый лист, в котором есть все нужные колонки. Ячейки читаются как отформатированный текст
 * (так их видит оператор в Excel). Ошибка содержит понятное объяснение, что не так с файлом.
 */
export async function readDeliveryFile(file: File, kind: DeliveryFileKind): Promise<SheetTable> {
  const spec = FILE_SPECS[kind];
  const XLSX = await import('xlsx');

  let workbook: WorkBook;
  try {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(buffer, { type: 'array', cellDates: true, cellNF: false, cellText: false });
  } catch (error) {
    throw new Error('Не удалось прочитать файл. Проверьте, что это Excel-файл (.xlsx или .xls)');
  }

  let missing: string[] = spec.required;
  for (let i = 0; i < workbook.SheetNames.length; i += 1) {
    const worksheet = workbook.Sheets[workbook.SheetNames[i]];
    // Пустые строки сохраняются, а чтение идет с самой первой строки листа: номер строки с заголовками
    // считается так, как его видит оператор в Excel, даже если сверху пусто
    const cells = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
      header: 1,
      range: 0,
      blankrows: true,
      defval: '',
      raw: false
    });
    if (cells.length <= spec.headerRow) continue;

    const parsed = parseSheet(cells, spec);
    if ('missing' in parsed) {
      missing = parsed.missing;
      continue;
    }
    if (parsed.rows.length === 0) throw new Error(`В файле ${spec.title} нет строк с данными`);
    return parsed;
  }

  throw new Error(`В файле ${spec.title} не найдены колонки: ${missing.join(', ')}`);
}

/** Выгрузка обработанной выписки: имя файла и лист те же, что были в прежней версии экрана */
export async function exportDeliveryExcel(headers: string[], rows: SheetRow[]): Promise<void> {
  const XLSX = await import('xlsx');
  const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, 'КаспийНовый.xlsx');
}
